import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight, KeyRound } from 'lucide-react';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { LockIcon } from '@/assets/icons/LockIcon';
import { SectionCard } from '@/components/common/SectionCard';
import { ShieldNote } from '@/components/common/ShieldNote';

const F = UI_STRINGS.FORGOT_PASSWORD;
const ICON_SIZE = 16;
const ARROW_SIZE = 18;

// Mirrors InviteSignupPage's acceptInviteSchema password field exactly - same
// shape, same messages. No second, divergent password policy and no
// strength meter (InviteSignupPage has neither).
const passwordSchema = z
  .object({
    newPassword: z.string().min(8, F.NEW_PASSWORD_MIN),
    confirmNewPassword: z.string().min(1, F.CONFIRM_NEW_PASSWORD_REQUIRED),
  })
  .refine((values) => values.newPassword === values.confirmNewPassword, {
    message: F.PASSWORD_MISMATCH,
    path: ['confirmNewPassword'],
  });
export type ForgotPasswordPasswordValues = z.infer<typeof passwordSchema>;

export interface ForgotPasswordPasswordStepProps {
  /** The code has already been collected on the code step; this step only
   * submits the new password. ForgotPasswordPage combines these values with
   * the held email + code before calling the single completeReset mutation. */
  onSubmit: (values: ForgotPasswordPasswordValues) => void;
  isPending: boolean;
  errorMessage?: string;
}

/**
 * The reset flow's final, submitting step: new password and confirm
 * password, full width and stacked, each with a visible label. Owns its own
 * form state - unmounting (e.g. when the flow moves to the `failed` step)
 * discards it, so a fresh mount never shows stale values.
 */
export function ForgotPasswordPasswordStep({
  onSubmit,
  isPending,
  errorMessage,
}: ForgotPasswordPasswordStepProps) {
  const { register, handleSubmit, formState } = useForm<ForgotPasswordPasswordValues>({
    resolver: zodResolver(passwordSchema),
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-6">
      <SectionCard title={F.RESET_BADGE} icon={<KeyRound size={20} />}>
        {errorMessage && (
          <p role="alert" className="mb-4 text-center text-sm text-danger">
            {errorMessage}
          </p>
        )}
        <div className="flex flex-col gap-6">
          <Input
            {...register('newPassword')}
            label={F.NEW_PASSWORD_LABEL}
            showLabel
            type="password"
            autoComplete="new-password"
            placeholder={F.NEW_PASSWORD_PLACEHOLDER}
            icon={<LockIcon size={ICON_SIZE} />}
            error={formState.errors.newPassword?.message}
          />
          <Input
            {...register('confirmNewPassword')}
            label={F.CONFIRM_NEW_PASSWORD_LABEL}
            showLabel
            type="password"
            autoComplete="new-password"
            placeholder={F.NEW_PASSWORD_PLACEHOLDER}
            icon={<LockIcon size={ICON_SIZE} />}
            error={formState.errors.confirmNewPassword?.message}
          />
        </div>
      </SectionCard>

      <div className="flex flex-col gap-4">
        <ShieldNote>{F.ENCRYPTION_NOTE}</ShieldNote>
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="navy"
            disabled={isPending}
            className="inline-flex items-center gap-2 px-8 py-3.5 text-base drop-shadow-md"
          >
            {isPending ? F.RESET_SUBMITTING : F.RESET_SUBMIT}
            <ArrowRight size={ARROW_SIZE} aria-hidden="true" />
          </Button>
        </div>
      </div>
    </form>
  );
}
